import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import CloudDownloadIcon from "@material-ui/icons/CloudDownload"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import { useEffect, useRef, useState } from "react"
import { alertStart } from "../../../routes"
import { exportarCardapio, importarCardapio } from "../../../services/importacaoCardapio"
import { ObterCardapioNovo } from "../../../services/clientefiel"
import { buscarFihcasTecnicas } from "../../../services/fichaTecnica"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { useCardapioContext } from "./CardapioContext"
import { baixarCsvCardapio } from "../utils/exportarCsv"
import { csvTemIds, importarCsvCardapio, substituirCardapioComCsv } from "../utils/importarCsv"

const useStyles = makeStyles(theme => ({
    container: {
        width: "100%",
        maxWidth: 900,
        margin: "auto",
        padding: theme.spacing(2),
        textAlign: "left",
        [theme.breakpoints.up("md")]: {
            paddingTop: "6rem",
        },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    legenda: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(2),
    },
    grupoBotoes: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1.5),
    },
    botaoExportar: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 600,
        color: "#4caf50",
        borderColor: "#4caf50",
    },
    botaoImportar: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 600,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 4px 12px rgba(var(--cor-botao-salvar-rgb),0.35)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    passos: {
        color: "#2c3e50",
        "& li": { marginBottom: theme.spacing(0.5) },
    },
    progressoBarra: {
        height: 10,
        borderRadius: 5,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    exemploImg: {
        width: "100%",
        maxWidth: 800,
        position: "relative",
        borderRadius: 8,
        marginTop: theme.spacing(1),
    },
}))

export default function ImportacaoExportacao() {
    const classes = useStyles()
    const { estabelecimento } = useCardapioContext()
    const setLoading = useLoadingStore(s => s.setLoading)
    const perfil = useAppStore(s => s.configuracoes?.user?.perfil)
    const ehClienteFiel = perfil === "CLIENTEFIEL"

    const [conteudo, setConteudo] = useState("")
    const [fotosEmPng, setFotosEmPng] = useState(false)

    // Importação CSV com upsert por id (por arquivo OU colando o conteúdo).
    const inputCsvRef = useRef(null)
    const [arquivoCsv, setArquivoCsv] = useState(null)
    const [textoCsv, setTextoCsv] = useState("")
    const [confirmarCsv, setConfirmarCsv] = useState(false)
    const [resultadoCsv, setResultadoCsv] = useState(null)
    // Aviso quando o CSV nao tem coluna Id preenchida (importar duplicaria o cardapio).
    const [avisoSemId, setAvisoSemId] = useState(false)
    const [textoImportPendente, setTextoImportPendente] = useState("")

    // Andamento da importação em curso ({ titulo, percentual, etapa }) — null quando ociosa.
    const [progresso, setProgresso] = useState(null)
    const importando = !!progresso

    // Fechar/recarregar a aba no meio da importação CSV interrompe as linhas restantes
    // (ela roda aqui no navegador). Pede confirmação enquanto houver importação em curso.
    useEffect(() => {
        if (!importando) return undefined
        const aoSair = e => {
            e.preventDefault()
            e.returnValue = ""
        }
        window.addEventListener("beforeunload", aoSair)
        return () => window.removeEventListener("beforeunload", aoSair)
    }, [importando])

    const iniciarProgresso = titulo => setProgresso({ titulo, percentual: 0, etapa: "Iniciando" })
    const atualizarProgresso = ({ percentual, etapa }) =>
        setProgresso(p => (p ? { ...p, percentual, etapa } : p))

    const autorizacao = localStorage.getItem("chaveSistema")

    const baixarArquivo = (texto, nomeArquivo) => {
        const blob = new Blob(["﻿" + texto], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = nomeArquivo || "produtos.csv"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const handleExportar = async (separador, agrupamento) => {
        if (!autorizacao || !estabelecimento?.id) return
        try {
            setLoading(true)
            const response = await exportarCardapio(
                autorizacao,
                estabelecimento.id,
                separador,
                agrupamento
            )
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao exportar o cardápio", "error")
                return
            }
            baixarArquivo(response.conteudo, response.nomeArquivo)
            alertStart("Exportação realizada com sucesso!", "success")
        } catch (error) {
            alertStart("Erro ao exportar o cardápio", "error")
        } finally {
            setLoading(false)
        }
    }

    // Exporta o CSV com IDs + referência de ficha técnica, gerado no front a partir
    // do cardápio atual. Busca os dados sob demanda (esta tela não carrega o cardápio).
    const handleExportarCsv = async () => {
        if (!autorizacao || !estabelecimento?.id) return
        try {
            setLoading(true)
            const cardapio = await ObterCardapioNovo(autorizacao, {
                idEstabelecimento: estabelecimento.id,
                token: "a~I?;;+_@!AL^",
            })
            if (cardapio?.retornoErro) {
                alertStart(cardapio.mensagem || "Erro ao obter o cardápio", "error")
                return
            }
            let fichas = []
            try {
                const resp = await buscarFihcasTecnicas(estabelecimento.id, autorizacao)
                if (Array.isArray(resp)) fichas = resp
            } catch (e) {
                // Ficha técnica é só referência: se falhar, exporta sem essas colunas preenchidas.
            }
            baixarCsvCardapio(cardapio.categorias, fichas, "cardapio.csv")
            alertStart("Exportação realizada com sucesso!", "success")
        } catch (error) {
            alertStart("Erro ao exportar o cardápio", "error")
        } finally {
            setLoading(false)
        }
    }

    // Le o arquivo detectando a codificacao: tenta UTF-8 estrito e, se falhar
    // (CSV salvo pelo Excel em ANSI/Latin-1, ou backup antigo do S3 em ISO-8859-1),
    // cai para windows-1252. Ler tudo como UTF-8 fixo quebrava os acentos desses casos.
    const lerArquivoTexto = file =>
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                const buffer = reader.result
                try {
                    resolve(new TextDecoder("utf-8", { fatal: true }).decode(buffer))
                } catch (e) {
                    try {
                        resolve(new TextDecoder("windows-1252").decode(buffer))
                    } catch (e2) {
                        resolve(new TextDecoder().decode(buffer))
                    }
                }
            }
            reader.onerror = () => reject(reader.error)
            reader.readAsArrayBuffer(file)
        })

    // Executa a importacao a partir do texto ja lido. substituir=true apaga o cardapio
    // atual (soft delete) e recria so com os itens do arquivo.
    const executarImport = async (texto, substituir = false) => {
        try {
            iniciarProgresso("Importando CSV")
            const params = { texto, estabelecimento, autorizacao, onProgresso: atualizarProgresso }
            const resultado = substituir
                ? await substituirCardapioComCsv(params)
                : await importarCsvCardapio(params)
            setResultadoCsv(resultado)
            setArquivoCsv(null)
            setTextoCsv("")
            if (inputCsvRef.current) inputCsvRef.current.value = ""
            const { criados, atualizados, erros } = resultado
            if (erros.length === 0) {
                alertStart(
                    `Importação concluída: ${criados} criado(s), ${atualizados} atualizado(s).`,
                    "success"
                )
            } else {
                alertStart(
                    `Importação com ${erros.length} erro(s). ${criados} criado(s), ${atualizados} atualizado(s).`,
                    "warning"
                )
            }
        } catch (error) {
            alertStart("Erro ao importar o CSV", "error")
        } finally {
            setProgresso(null)
        }
    }

    const handleImportarCsv = async () => {
        setConfirmarCsv(false)
        if (!autorizacao || !estabelecimento?.id) return
        // Usa o arquivo selecionado; se não houver, usa o conteúdo colado.
        if (!arquivoCsv && !textoCsv.trim()) return
        let texto
        try {
            setLoading(true)
            texto = arquivoCsv ? await lerArquivoTexto(arquivoCsv) : textoCsv
        } catch (e) {
            alertStart("Erro ao ler o arquivo", "error")
            return
        } finally {
            setLoading(false)
        }
        // Sem coluna Id (ou todos vazios): importar duplicaria. Avisa e oferece opcoes.
        if (!csvTemIds(texto)) {
            setTextoImportPendente(texto)
            setAvisoSemId(true)
            return
        }
        await executarImport(texto, false)
    }

    const handleImportar = async () => {
        if (!autorizacao || !estabelecimento?.id) return
        if (!conteudo.trim()) {
            alertStart("Cole o conteúdo do cardápio antes de importar.", "error")
            return
        }
        try {
            iniciarProgresso("Importando cardápio")
            const response = await importarCardapio(
                autorizacao,
                estabelecimento.id,
                conteudo,
                fotosEmPng,
                atualizarProgresso
            )
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao importar o cardápio", "error")
                return
            }
            setConteudo("")
            alertStart(response.mensagem || "Importação realizada com sucesso!", "success")
        } catch (error) {
            alertStart("Erro ao importar o cardápio", "error")
        } finally {
            setProgresso(null)
        }
    }

    return (
        <Box className={classes.container}>
            {/* ─── Exportação ─── */}
            <Paper className={classes.configCard}>
                <Box className={classes.cardHeader}>
                    <CloudDownloadIcon className={classes.iconHeader} />
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        Exportar cardápio
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    <Typography variant="body2" className={classes.legenda}>
                        Baixe o cardápio atual em um arquivo para editar no Excel (ou similar).
                    </Typography>
                    <Box className={classes.grupoBotoes}>
                        <Button
                            variant="contained"
                            className={classes.botaoImportar}
                            onClick={handleExportarCsv}
                        >
                            Exportar CSV (com IDs)
                        </Button>
                        <Button
                            variant="outlined"
                            className={classes.botaoExportar}
                            onClick={() => handleExportar("ponto-virgula", false)}
                        >
                            Exportar Simples
                        </Button>
                        <Button
                            variant="outlined"
                            className={classes.botaoExportar}
                            onClick={() => handleExportar("ponto-virgula", true)}
                        >
                            Exportar Avançado
                        </Button>
                        {ehClienteFiel && (
                            <Button
                                variant="outlined"
                                className={classes.botaoExportar}
                                onClick={() => handleExportar("tab", false)}
                            >
                                Exportar com Tabs
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* ─── Importação CSV (com IDs, upsert) ─── */}
            <Paper className={classes.configCard}>
                <Box className={classes.cardHeader}>
                    <CloudUploadIcon className={classes.iconHeader} />
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        Importar CSV (com IDs)
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    <Typography variant="body2" className={classes.legenda}>
                        Envie o arquivo <b>.csv</b> (ou cole o conteúdo dele abaixo) gerado por
                        "Exportar CSV (com IDs)". Linhas com <b>Id preenchido são atualizadas</b>;
                        linhas com <b>Id vazio criam um novo item</b>. O cardápio atual não é
                        substituído. Recomendado testar primeiro em um estabelecimento de teste.
                    </Typography>
                    <input
                        ref={inputCsvRef}
                        type="file"
                        accept=".csv,text/csv"
                        style={{ display: "none" }}
                        onChange={e => setArquivoCsv(e.target.files?.[0] ?? null)}
                    />
                    <Box
                        className={classes.grupoBotoes}
                        style={{ alignItems: "center", marginBottom: 12 }}
                    >
                        <Button
                            variant="outlined"
                            className={classes.botaoExportar}
                            onClick={() => inputCsvRef.current?.click()}
                        >
                            Selecionar arquivo
                        </Button>
                        <Typography variant="body2" style={{ color: "#2c3e50" }}>
                            {arquivoCsv ? arquivoCsv.name : "Nenhum arquivo selecionado"}
                        </Typography>
                    </Box>
                    <Typography variant="body2" className={classes.legenda}>
                        Ou cole aqui o conteúdo do arquivo (abra o .csv no bloco de notas e cole
                        o texto — <b>não</b> copie as células do Excel):
                    </Typography>
                    <TextField
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={8}
                        placeholder="Tipo;Nome;Descrição;...;Id;Versao;..."
                        value={textoCsv}
                        onChange={e => setTextoCsv(e.target.value)}
                        disabled={!!arquivoCsv}
                    />
                    <Box className={classes.grupoBotoes} style={{ marginTop: 8 }}>
                        <Button
                            className={classes.botaoImportar}
                            disabled={importando || (!arquivoCsv && !textoCsv.trim())}
                            onClick={() => setConfirmarCsv(true)}
                        >
                            Importar CSV (com IDs)
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* ─── Importação (backend, substitui tudo) ─── */}
            <Paper className={classes.configCard}>
                <Box className={classes.cardHeader}>
                    <CloudUploadIcon className={classes.iconHeader} />
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        Importar cardápio
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    <Typography variant="body2" className={classes.legenda}>
                        Cole abaixo o conteúdo do cardápio (copiado do Excel) e clique em Importar.
                        O cardápio atual será inativado e substituído pelo novo.
                    </Typography>
                    <TextField
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={14}
                        placeholder="Cole aqui o conteúdo do cardápio..."
                        value={conteudo}
                        onChange={e => setConteudo(e.target.value)}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={fotosEmPng}
                                onChange={e => {
                                    const checked = e.target.checked
                                    setFotosEmPng(checked)
                                }}
                                style={{ color: "#4caf50" }}
                            />
                        }
                        label="Fotos em PNG"
                    />
                    <Box className={classes.grupoBotoes} style={{ marginTop: 8 }}>
                        <Button
                            className={classes.botaoImportar}
                            onClick={handleImportar}
                            disabled={importando}
                        >
                            Importar
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* ─── Tutorial ─── */}
            <Paper className={classes.configCard}>
                <Box className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        Passos para preenchimento
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    <ol className={classes.passos}>
                        <li>Abra o Microsoft Excel (ou similar) no seu computador.</li>
                        <li>
                            Insira o seu cardápio da seguinte forma (uma coluna por informação):
                        </li>
                    </ol>
                    <ul className={classes.passos}>
                        <li>
                            Tipo (C-Categoria, P-Produto, GA-Grupo de Adicionais, GO-Grupo de
                            Opções, O-Opção)
                        </li>
                        <li>Nome</li>
                        <li>Descrição</li>
                        <li>Valor</li>
                        <li>Código</li>
                        <li>Quantidade mínima (apenas para grupo de adicionais GA)</li>
                        <li>Quantidade máxima (apenas para grupo de adicionais GA)</li>
                        <li>
                            Considerar maior valor (apenas GO, usar em caso de pizza meio a meio)
                        </li>
                        <li>Disponível ('Sim' ou 'Não')</li>
                        <li>
                            Dia da semana disponível (SEGUNDA, TERCA, QUARTA, QUINTA, SEXTA, SABADO
                            ou DOMINGO)
                        </li>
                        <li>Turno disponível (APENAS_DIURNO ou APENAS_NOTURNO)</li>
                        <li>Permite observação ('Sim' ou 'Não')</li>
                        <li>Url Foto (url com a fotografia do produto)</li>
                    </ul>
                    <ol className={classes.passos} start={3}>
                        <li>Copie e cole o conteúdo na caixa de texto acima.</li>
                        <li>Clique em Importar.</li>
                        <li>Verifique o seu cardápio pelo aplicativo.</li>
                    </ol>

                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 16 }}>
                        Exemplo no Excel
                    </Typography>
                    <img
                        className={classes.exemploImg}
                        src="https://s3-sa-east-1.amazonaws.com/clientefielsp/CardapioPreenchidoExemplo.png"
                        alt="Exemplo de cardápio preenchido no Excel"
                    />

                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 16 }}>
                        Exemplo conteúdo colado
                    </Typography>
                    <img
                        className={classes.exemploImg}
                        src="https://s3-sa-east-1.amazonaws.com/clientefielsp/CardapioColadoExemplo.png"
                        alt="Exemplo de conteúdo colado"
                    />
                </Box>
            </Paper>

            {/* Andamento da importação (não fecha até concluir) */}
            <Dialog open={importando} maxWidth="sm" fullWidth disableEscapeKeyDown>
                <DialogTitle>{progresso?.titulo}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ color: "#2c3e50" }}>
                        {progresso?.etapa}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progresso?.percentual ?? 0}
                        className={classes.progressoBarra}
                    />
                    <Typography variant="body2" align="right" style={{ fontWeight: 600 }}>
                        {progresso?.percentual ?? 0}%
                    </Typography>
                    <Typography variant="caption" className={classes.legenda} component="p">
                        Cardápios grandes podem levar alguns minutos. Mantenha esta página aberta
                        até a conclusão.
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* Confirmação da importação CSV */}
            <Dialog open={confirmarCsv} onClose={() => setConfirmarCsv(false)} maxWidth="xs">
                <DialogTitle>Confirmar importação</DialogTitle>
                <DialogContent>
                    <Typography>
                        Isso vai <b>criar ou atualizar</b> itens do cardápio a partir do arquivo
                        <b> {arquivoCsv?.name}</b>. Deseja continuar?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmarCsv(false)}>Cancelar</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleImportarCsv}
                    >
                        Importar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Aviso: arquivo sem coluna Id preenchida (importar duplicaria) */}
            <Dialog open={avisoSemId} onClose={() => setAvisoSemId(false)} maxWidth="sm">
                <DialogTitle>Arquivo sem Id</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        O arquivo não tem a coluna <b>Id</b> preenchida. Importando assim, cada
                        linha vira um <b>item novo</b> — o cardápio atual <b>não é atualizado</b> e
                        pode ficar <b>duplicado</b>. Para atualizar os itens já existentes, use o
                        arquivo gerado por <b>"Exportar CSV (com IDs)"</b>.
                    </Typography>
                    <Typography style={{ marginTop: 8 }}>Como deseja continuar?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAvisoSemId(false)}>Cancelar</Button>
                    <Button
                        onClick={() => {
                            setAvisoSemId(false)
                            executarImport(textoImportPendente, false)
                        }}
                    >
                        Importar mesmo assim
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                            setAvisoSemId(false)
                            executarImport(textoImportPendente, true)
                        }}
                    >
                        Apagar e recriar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resumo do resultado da importação CSV */}
            <Dialog
                open={!!resultadoCsv}
                onClose={() => setResultadoCsv(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Resultado da importação</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {resultadoCsv?.criados ?? 0} item(ns) criado(s),{" "}
                        {resultadoCsv?.atualizados ?? 0} atualizado(s).
                    </Typography>
                    {resultadoCsv?.erros?.length > 0 && (
                        <>
                            <Typography style={{ fontWeight: 600, marginTop: 8, color: "var(--cor-erro)" }}>
                                {resultadoCsv.erros.length} erro(s):
                            </Typography>
                            <ul style={{ maxHeight: 240, overflow: "auto" }}>
                                {resultadoCsv.erros.map((erro, i) => (
                                    <li key={i}>
                                        <Typography variant="body2">{erro}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setResultadoCsv(null)}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
